import { useEffect, useRef, useState } from "react";
import { ListBigNumber, SupportBoard } from "./components";
import { BigNumberSupport } from "./components/ListBigNumber";
import { LoadMoreButton, SelectOption } from "@rio-cloud/rio-uikit";
import { useIntl } from "react-intl";
import { fetchOccurrences, fetchOccurrencesBigNumber, fetchSupportAssignedUsers, fetchSupportRegionals } from "../../api/tas";
import { IOccurrence } from "./types";
import { useSearchParams } from "react-router";
import { CLEAR_SORT_OPTION_ID, parseSortOption } from "./components/SortOptionsSupport";
import { assignmentParamsToSelection, assignmentSelectionToParams } from "./constants";

interface Props {
    assetIds: string[]
}

const SupportPage = ({ assetIds }: Props) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const type = (searchParams.get('type') as BigNumberSupport) || 'all';
    const [data, setData] = useState<IOccurrence[]>([]);
    const supportType = searchParams.get('supportType') || 'VOLKSTOTAL';
    const search = searchParams.get('search') || ''
    const dn = searchParams.get('dn') || '';
    const responsible = searchParams.get('responsible') || '';
    const [total, setTotal] = useState(0);
    const [size, setSize] = useState(30);
    const [isLoading, setIsLoading] = useState(true);
    const [optionsRegionals, setOptionsRegionals] = useState<SelectOption[]>([]);
    const [optionsAssigned, setOptionsAssigned] = useState<SelectOption[]>([]);
    const regional = searchParams.get('regional') || '';
    const assigned = searchParams.get('assigned') || '';
    const assignedToId = searchParams.get('assignedToId') || '';
    const assignedSelection = assignmentParamsToSelection(assigned, assignedToId);
    const sortBy = searchParams.get('sortBy') || '';
    const sortDirection = searchParams.get('sortDirection') || '';
    const [bigNumber, setBigNumber] = useState({
        OPEN: 0,
        IN_ANALYSIS: 0,
        ANSWERED: 0,
        FINISHED: 0,
    });

    const intl = useIntl();

    const fetchList = async () => {
        try {
            const res = await fetchOccurrences({
                status: type === 'all' ? null : type,
                page: 0,
                size: size,
                searchTerm: search,
                supportType: supportType === 'all' ? null : supportType,
                dn: dn,
                responsible: responsible,
                regional: regional === 'all' ? null : regional,
                assigned: assigned || undefined,
                assignedToId: assignedToId || undefined,
                sortBy: sortBy || null,
                sortDirection: sortDirection || null,
            });
            setData(res.content);
            setTotal(res.totalElements);

        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    const fetchBigNumberData = async () => {
        const res = await fetchOccurrencesBigNumber({
            searchTerm: search,
            supportType: supportType === 'all' ? null : supportType,
            dn: dn,
            responsible: responsible,
            regional: regional === 'all' ? null : regional,
            assigned: assigned || undefined,
            assignedToId: assignedToId || undefined,
        });
        setBigNumber({
            OPEN: res.OPEN.count,
            IN_ANALYSIS: res.IN_ANALYSIS.count,
            ANSWERED: res.ANSWERED.count,
            FINISHED: res.FINISHED.count,
        });
    }


    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const updateParam = (key: string, value: string) => {
        setSearchParams(prev => {
            const params = new URLSearchParams(prev);

            if (!value) {
                params.delete(key);
            } else {
                params.set(key, value);
            }

            return params;
        });
    };

    const handleSearch = (text: string) => {
        updateParam('search', text);
    };

    const handleListDebounce = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            fetchList();
            fetchBigNumberData();
        }, 500);
    };

    const handleDn = (text: string) => {
        updateParam('dn', text);
    };

    const handleResponsible = (text: string) => {
        updateParam('responsible', text);
    };

    const handleSupportType = (value: string) => {
        updateParam('supportType', value);
    };


    const handleRegional = (value: string) => {
        updateParam('regional', value);
    };

    const handleAssignedChange = (values: string[]) => {
        const { assigned: nextAssigned, assignedToId: nextAssignedToId } = assignmentSelectionToParams(values);

        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);

            if (nextAssigned) {
                params.set('assigned', nextAssigned);
            } else {
                params.delete('assigned');
            }

            if (nextAssignedToId) {
                params.set('assignedToId', nextAssignedToId);
            } else {
                params.delete('assignedToId');
            }

            return params;
        });
    };

    const handleSort = (optionId: string) => {
        if (optionId === CLEAR_SORT_OPTION_ID) {
            setSearchParams(prev => {
                const params = new URLSearchParams(prev);
                params.delete('sortBy');
                params.delete('sortDirection');
                return params;
            });
            return;
        }

        const { sortBy: newSortBy, sortDirection: newSortDirection } = parseSortOption(optionId);
        setSearchParams(prev => {
            const params = new URLSearchParams(prev);
            params.set('sortBy', newSortBy);
            params.set('sortDirection', newSortDirection);
            return params;
        });
    };

    const getTranslatedAssignmentFilterOptions = (): SelectOption[] => [
        {
            id: 'ASSIGNED',
            label: intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.assignment.assigned' }),
        },
        {
            id: 'UNASSIGNED',
            label: intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.assignment.unassigned' }),
        },
        {
            id: 'all',
            label: intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.assignment.all' }),
        },
    ];

    const fetchRegionals = async () => {
        const res = await fetchSupportRegionals();
        if (res?.content) {
            const all = {
                id: 'all',
                label: intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.supportType.all' }),
            };

            const map = res?.content?.map((res: { id: number, name: string }) => {
                return {
                    id: res.id.toString(),
                    label: res.name,
                }
            })
            setOptionsRegionals([...map, all]);
        }
    };

    const fetchAssignedUsers = async () => {
        const users = await fetchSupportAssignedUsers();
        const userOptions = users.map((user) => ({
            id: user.id,
            label: user.name,
        }));

        setOptionsAssigned([...getTranslatedAssignmentFilterOptions(), ...userOptions]);
    };

    const handleType = (newType: BigNumberSupport) => {
        setSearchParams(prev => {
            prev.set('type', newType);
            return prev;
        });
    };

    const onLoadMore = () => {
        const nextSize = size + 30;

        setSize(nextSize > total ? total : nextSize);
    }

    useEffect(() => {
        fetchRegionals();
        fetchAssignedUsers();
    }, [intl.locale]);

    useEffect(() => {
        fetchList()
    }, [type, bigNumber, supportType, dn, responsible, size, regional, assigned, assignedToId, sortBy, sortDirection]);

    useEffect(() => {
        fetchBigNumberData();
    }, [supportType, dn, responsible, regional, assigned, assignedToId]);

    useEffect(() => {
        handleListDebounce();
    }, [search])


    useEffect(() => {
        if (!searchParams.get('supportType')) {
            setSearchParams(
                {
                    supportType: 'VOLKSTOTAL',
                    type: 'all',
                    regional: 'all',
                },
                { replace: true }
            );
        }
    }, []);

    return (
        <div className="margin-y-20">
            <ListBigNumber
                selected={type}
                analyze={bigNumber.IN_ANALYSIS}
                dealer={bigNumber.ANSWERED}
                finished={bigNumber.FINISHED}
                pending={bigNumber.OPEN}
                handleType={handleType}
            />
            <SupportBoard
                isLoading={isLoading}
                data={data}
                search={search}
                dn={dn}
                responsible={responsible}
                optionsRegionals={optionsRegionals}
                optionsAssigned={optionsAssigned}
                regional={regional}
                sortBy={sortBy}
                sortDirection={sortDirection}
                handleSearch={handleSearch}
                handleSupportType={handleSupportType}
                handleDn={handleDn}
                handleResponsible={handleResponsible}
                handleRegional={handleRegional}
                handleSort={handleSort}
                supportType={supportType}
                handleAssignedChange={handleAssignedChange}
                assignedSelection={assignedSelection}
            />
            <LoadMoreButton
                loaded={data?.length < 30 ? data.length : size}
                total={total}
                onLoadMore={onLoadMore}
                loadMoreMessage={intl.formatMessage({ id: "intl-msg:monitoring.loadMore" })}
                noMoreMessage={intl.formatMessage({ id: "intl-msg:monitoring.allLoaded" })}
            />
        </div>
    )
};

export default SupportPage
